/**
 * Main Dashboard Section
 *
 * The operational dashboard shown on the admin portal's landing page and, for
 * the packhouse manager, on the gate portal's. Lives here rather than in either
 * app so the two cannot drift into two different dashboards, and because it
 * composes several domains — no single domain may own it without importing a
 * sibling.
 *
 * Everything portal-specific arrives as props: the balance figure (the gate
 * portal has no finance slice) and what a click on the orders KPI should do.
 *
 * Scoping is identical in both portals and comes from the request, not from
 * here: the http client sends X-Factory-Id for the current selection, and the
 * server force-scopes a user who owns a factory to their own plant.
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from '@spcc/core'
import { selectUserRoleType } from '@spcc/domains/auth'
import {
  DashboardStatsSection,
  resolveDateRange,
  selectDateRange,
} from '@spcc/domains/dashboards/customer-portal'
import {
  PlantTruckStatusCard,
  queueActions,
  selectActiveEntries,
  selectEntryLoading,
} from '@spcc/domains/queue'
import {
  queueDashboardActions,
  selectProductBreakdown,
  selectStuckOrders,
  StuckOrdersCard,
} from '@spcc/domains/dashboards/queue'

const OrdersChart = lazy(() =>
  import('@spcc/domains/dashboards/customer-portal').then((m) => ({
    default: m.OrdersChart,
  })),
)
const RankingsSection = lazy(() =>
  import('@spcc/domains/dashboards/customer-portal').then((m) => ({
    default: m.RankingsSection,
  })),
)

/** The truck panel is a live monitor; the queue moves on a minutes scale. */
const QUEUE_POLL_INTERVAL_MS = 30_000

export interface MainDashboardSectionProps {
  /** Balance for the KPI row. Portals without a finance slice pass null. */
  customerBalance?: number | null
  customerBalanceCurrency?: string
  /** Where the orders KPI leads. Omit to make it inert. */
  onOrdersClick?: () => void
  /**
   * Re-fetch whenever this changes — the tenant or plant the portal is showing.
   * Not sent as a filter: the http client already carries the selection in its
   * headers, this only says "the answer will be different now".
   */
  scopeKey?: string | null
  /** Ranking cards to hide for roles that should not see them. */
  showTopCustomers?: boolean
  showFactoryPerformance?: boolean
}

/** Roles the stuck-orders diagnostic is shown to — the controller enforces the same list. */
const STUCK_ORDER_ROLES = ['super_admin', 'admin', 'sales_manager', 'head_of_sales', 'sales_office_user']

export function MainDashboardSection({
  customerBalance = null,
  customerBalanceCurrency = 'SAR',
  onOrdersClick,
  scopeKey = null,
  showTopCustomers = true,
  showFactoryPerformance = true,
}: MainDashboardSectionProps) {
  const dispatch = useDispatch()

  const dashboardDateRange = useSelector(selectDateRange)
  const activeQueueEntries = useSelector(selectActiveEntries)
  const queueLoading = useSelector(selectEntryLoading)
  const stuckOrders = useSelector(selectStuckOrders)
  // Stuck orders is a diagnostic; the endpoint refuses anyone outside
  // STUCK_ORDER_ROLES, so no other role should even ask.
  const roleType = useSelector(selectUserRoleType)
  const canSeeStuckOrders = STUCK_ORDER_ROLES.includes(roleType as string)
  const productBreakdown = useSelector(selectProductBreakdown)
  const [queueRefreshedAt, setQueueRefreshedAt] = useState<string | null>(null)

  // Live truck queue, polled — it is a monitor and moves minute to minute.
  useEffect(() => {
    const refresh = () => {
      dispatch(queueActions.fetchQueueEntries({ search: '', status: 'active' }))
      // Same cadence, same reason: an order that got stuck a minute ago is one
      // somebody can still rescue today.
      if (canSeeStuckOrders) dispatch(queueDashboardActions.fetchStuckOrders())
      setQueueRefreshedAt(new Date().toISOString())
    }
    refresh()
    const timer = setInterval(refresh, QUEUE_POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [dispatch, scopeKey, canSeeStuckOrders])

  /**
   * Queue figures per product, for the Top Products ranking. This used to be its
   * own "Orders by Product" card; it is now three more columns (and three more
   * tooltip lines) on the ranking that already lists the same products, joined
   * on variant documentId — which is what both endpoints key a product by.
   *
   * Mapped here rather than in the ranking: that card lives in the customer
   * portal domain and may not import the queue domain. This page composes both.
   */
  const productQueueStats = useMemo(
    () =>
      (productBreakdown?.products ?? []).map((p) => ({
        productId: p.variantId,
        ordersHandled: p.totalHandled,
        avgLoadingTimeMinutes: p.avgLoadingTimeMinutes,
        avgWaitTimeMinutes: p.avgWaitTimeMinutes,
      })),
    [productBreakdown],
  )

  // Product breakdown is a period aggregation, not a live figure — no polling.
  // It follows THIS page's date picker: left to itself the queue-dashboard slice
  // would answer from its own default range, so the numbers would silently
  // report a different period from every other card on the page.
  // (fetchProducts, not fetchAll: the latter fires seven aggregations for one card.)
  useEffect(() => {
    const { from, to } = resolveDateRange(dashboardDateRange)
    dispatch(queueDashboardActions.fetchProducts({ from, to }))
  }, [dispatch, scopeKey, dashboardDateRange])

  return (
    <div className="space-y-8" data-testid="dashboard">
      <DashboardStatsSection
        customerBalance={customerBalance}
        customerBalanceCurrency={customerBalanceCurrency}
        onOrdersClick={onOrdersClick}
        hideFinancialOverview
      />

      <Suspense fallback={null}>
        {/*
          The truck monitor is deliberately double height — the queue is the
          longest list on the page and the one people actually read.
          57.5rem = 2 x the 28rem chart beside it plus the 1.5rem grid gap, so it
          spans exactly where two stacked charts used to.
          This is an explicit height, not the fill-the-cell trick it used while
          two charts stacked beside it: with one chart in that column there is no
          two-row box left to fill, and an absolutely positioned card would
          collapse to the single chart's height.
          A definite `h-`, not `max-h-`: flex ratios divide FREE space, and a
          max-height leaves the card content-sized, so there is no free space and
          the 3:2 inside/outside split does nothing. `max-h-none` clears the
          component's own standalone 26rem cap.
        */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <PlantTruckStatusCard
            entries={activeQueueEntries}
            isLoading={queueLoading}
            lastRefreshed={queueRefreshedAt}
            className="h-[61.1rem] max-h-none"
          />
          {/*
            Order Activity's column, with the stuck-order list at the top of it:
            it is about orders like the chart under it, and an exception nobody
            is watching for has to be the first thing in the column, not
            something you scroll past the chart to find. It renders nothing at
            all when nothing is stuck, so on a clean day the column is exactly
            the chart, as it was.
          */}
          <div className="flex flex-col gap-6 min-w-0">
            {canSeeStuckOrders && <StuckOrdersCard data={stuckOrders} />}
            <div className="chart-hsl-vars">
              <OrdersChart />
            </div>
          </div>
        </div>

        <div className="chart-hsl-vars">
          <RankingsSection
            showTopCustomers={showTopCustomers}
            showFactoryPerformance={showFactoryPerformance}
            productQueueStats={productQueueStats}
          />
        </div>
      </Suspense>
    </div>
  )
}
